import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Row, Col, Form, Card } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { FaPlus, FaTrashAlt, FaSave, FaTimes } from "react-icons/fa";

export default function DailyExpenseCreate() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);

    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        comment: '',
        details: [
            { id: Date.now(), category_id: '', price: '' }
        ],
        payments: [
            { id: Date.now(), payment_method_id: '', amount: '' }
        ]
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = () => {
        const token = localStorage.getItem("authToken");
        setLoading(true);

        axios.get(`${process.env.REACT_APP_API_URL}/daily-expenses-create`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        .then(res => {
            setExpenseCategories(res.data.dailyExepenseCategory || []);
            setPaymentMethods(res.data.paymentMethods || []);
        })
        .finally(() => setLoading(false));
    };

    const handleMainFormChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleAddDetailRow = () => {
        setFormData(prev => ({
            ...prev,
            details: [...prev.details, { id: Date.now(), category_id: '', price: '' }]
        }));
    };

    const handleRemoveDetailRow = (id) => {
        if (formData.details.length === 1) return;
        setFormData(prev => ({
            ...prev,
            details: prev.details.filter(d => d.id !== id)
        }));
    };

    const handleDetailChange = (id, field, value) => {
        setFormData(prev => ({
            ...prev,
            details: prev.details.map(d =>
                d.id === id ? { ...d, [field]: value } : d
            )
        }));
    };

    const handleAddPaymentRow = () => {
        setFormData(prev => ({
            ...prev,
            payments: [...prev.payments, { id: Date.now(), payment_method_id: '', amount: '' }]
        }));
    };

    const handleRemovePaymentRow = (id) => {
        if (formData.payments.length === 1) return;
        setFormData(prev => ({
            ...prev,
            payments: prev.payments.filter(p => p.id !== id)
        }));
    };

    const handlePaymentChange = (id, field, value) => {
        setFormData(prev => ({
            ...prev,
            payments: prev.payments.map(p =>
                p.id === id ? { ...p, [field]: value } : p
            )
        }));
    };

    const calculateTotalAmount = () =>
        formData.details.reduce((sum, d) => sum + (parseFloat(d.price) || 0), 0);

    const calculateTotalPayment = () =>
        formData.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            date: formData.date,
            comment: formData.comment,
            details: formData.details,
            payments: formData.payments
        };

        const token = localStorage.getItem("authToken");

        await axios.post(`${process.env.REACT_APP_API_URL}/daily-expenses`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        });

        navigate('/daily-expenses');
    };

    if (loading) return <PageLayout>Loading...</PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-3">
                        <div className="mc-breadcrumb">
                            <h3>{t('daily expenses')}</h3>
                        </div>
                    </div>
                </Col>

                {/* LEFT SIDE */}
                <Col xl={8}>
                    <div className="expense-left">

                        {/* BASIC */}
                        <Card className="expense-card dashed">
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Control
                                            type="date"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleMainFormChange}
                                            className="custom-input"
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Control
                                            name="comment"
                                            placeholder="Main Comment"
                                            value={formData.comment}
                                            onChange={handleMainFormChange}
                                            className="custom-input"
                                        />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* DETAILS */}
                        <Card className="expense-card dashed">
                            <Card.Body>

                                {formData.details.map((detail) => (
                                    <div key={detail.id} className="expense-row">

                                        <Row>
                                            <Col md={5}>
                                                <Form.Select
                                                    value={detail.category_id}
                                                    onChange={(e) =>
                                                        handleDetailChange(detail.id, 'category_id', e.target.value)
                                                    }
                                                    className="custom-input"
                                                >
                                                    <option>Select Category</option>
                                                    {expenseCategories.map(c => (
                                                        <option key={c.id} value={c.id}>{c.name}</option>
                                                    ))}
                                                </Form.Select>
                                            </Col>

                                            <Col md={5}>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Amount"
                                                    value={detail.price}
                                                    onChange={(e) =>
                                                        handleDetailChange(detail.id, 'price', e.target.value)
                                                    }
                                                    className="custom-input"
                                                />
                                            </Col>

                                            <Col md={2}>
                                                <ButtonComponent
                                                    className="btn btn-danger"
                                                    onClick={() => handleRemoveDetailRow(detail.id)}
                                                >
                                                    <FaTrashAlt />
                                                </ButtonComponent>
                                            </Col>
                                        </Row>

                                    </div>
                                ))}

                                <ButtonComponent onClick={handleAddDetailRow}>
                                    <FaPlus /> Add More
                                </ButtonComponent>

                            </Card.Body>
                        </Card>

                    </div>
                </Col>

                {/* RIGHT SUMMARY */}
                <Col xl={4}>
                    <div className="expense-summary-card">

                        <h5>Summary</h5>

                        <div className="summary-item">
                            <span>Total:</span>
                            <strong>{calculateTotalAmount().toFixed(2)}</strong>
                        </div>

                        <hr />

                        {formData.payments.map((payment) => (
                            <div key={payment.id} className="payment-row">

                                <Form.Select
                                    value={payment.payment_method_id}
                                    onChange={(e) =>
                                        handlePaymentChange(payment.id, 'payment_method_id', e.target.value)
                                    }
                                >
                                    <option>Select Bank</option>
                                    {paymentMethods.map(m => (
                                        <option key={m.id} value={m.id}>{m.name}</option>
                                    ))}
                                </Form.Select>

                                <Form.Control
                                    type="number"
                                    value={payment.amount}
                                    onChange={(e) =>
                                        handlePaymentChange(payment.id, 'amount', e.target.value)
                                    }
                                />

                                <button
                                    className="btn-delete"
                                    onClick={() => handleRemovePaymentRow(payment.id)}
                                >
                                    <FaTrashAlt />
                                </button>
                            </div>
                        ))}

                        <button onClick={handleAddPaymentRow}>
                            + Add Payment
                        </button>

                    </div>
                </Col>

                {/* SUBMIT */}
                <Col xl={12} className="mt-3 text-end">
                    <ButtonComponent className="btn btn-primary" onClick={handleSubmit}>
                        <FaSave /> Save Daily Expense
                    </ButtonComponent>
                </Col>

            </Row>
        </PageLayout>
    );
}